/**
 * Math Editor Dialog - Shared Component
 * Modal dialog for viewing, selecting, and editing mathematical expressions
 * Now enhanced with MathLive integration and improved UX
 */

import React, { Suspense, lazy, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Alert,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Lazy load the enhanced version
const EnhancedMathEditorDialog = lazy(() =>
  import('./MathEditorDialog/EnhancedMathEditorDialog').then(module => ({
    default: module.default
  }))
);

interface MathExpression {
  id: string;
  latex: string;
  selected: boolean;
  spine?: any;
  context?: string;
}

interface MathEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (expressions: MathExpression[]) => Promise<void>;
  initialExpressions: MathExpression[];
  lessonName?: string;
  lessonGrade?: string;
  loading?: boolean;
  saving?: boolean;
}

export default function MathEditorDialog(props: MathEditorDialogProps) {
  const [useEnhanced, setUseEnhanced] = useState(true);
  const [enhancedLoadError, setEnhancedLoadError] = useState(false);

  // Handle enhanced version load error
  const handleEnhancedError = () => {
    console.warn('Failed to load enhanced math editor, falling back to basic version');
    setEnhancedLoadError(true);
    setUseEnhanced(false);
  };

  // Try to use enhanced version if supported and not errored
  if (useEnhanced && !enhancedLoadError) {
    return (
      <Suspense
        fallback={
          <Dialog open={props.open} maxWidth="md" fullWidth>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">🔢 Mathematical Expressions Editor</Typography>
                <IconButton onClick={props.onClose} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 4 }}>
                <CircularProgress />
                <Typography color="text.secondary">Loading enhanced math editor...</Typography>
                <Skeleton variant="rectangular" width="100%" height={200} />
              </Box>
            </DialogContent>
          </Dialog>
        }
      >
        <EnhancedMathEditorDialogWrapper {...props} onError={handleEnhancedError} />
      </Suspense>
    );
  }

  // Fallback to basic implementation
  return <BasicMathEditorDialog {...props} />;
}

// Wrapper to handle enhanced version errors
const EnhancedMathEditorDialogWrapper: React.FC<MathEditorDialogProps & { onError: () => void }> = (props) => {
  const { onError, ...dialogProps } = props;

  try {
    return <EnhancedMathEditorDialog {...dialogProps} />;
  } catch (error) {
    console.error('Enhanced math editor error:', error);
    onError();
    return null;
  }
};

// Basic fallback implementation (original functionality)
const BasicMathEditorDialog: React.FC<MathEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  initialExpressions,
  lessonName,
  lessonGrade,
  loading = false,
  saving = false,
}) => {
  const [expressions, setExpressions] = useState<MathExpression[]>(initialExpressions);
  const [newLatex, setNewLatex] = useState('');
  const [dirty, setDirty] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setExpressions(initialExpressions);
      setNewLatex('');
      setDirty(false);
    }
  }, [open, initialExpressions]);

  const handleSelectChange = (index: number, checked: boolean) => {
    const updated = [...expressions];
    updated[index].selected = checked;
    setExpressions(updated);
    setDirty(true);
  };

  const handleLatexChange = (index: number, value: string) => {
    const updated = [...expressions];
    updated[index].latex = value;
    updated[index].spine = undefined; // Clear spine data when edited
    setExpressions(updated);
    setDirty(true);
  };

  const handleAddExpression = () => {
    if (!newLatex.trim()) return;

    const newExpr: MathExpression = {
      id: `expr_new_${Date.now()}`,
      latex: newLatex.trim(),
      selected: true,
      context: '',
    };

    setExpressions([...expressions, newExpr]);
    setNewLatex('');
    setDirty(true);
  };

  const handleDeleteExpression = (index: number) => {
    const updated = expressions.filter((_, i) => i !== index);
    setExpressions(updated);
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await onSave(expressions);
      setDirty(false);
      // onClose will be called by the parent component after successful save
    } catch (error) {
      console.error('Failed to save expressions:', error);
    }
  };

  const handleClose = () => {
    if (dirty) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '90vh' } }}
      sx={{ zIndex: 1500 }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">🔢 Mathematical Expressions Editor</Typography>
            {lessonName && (
              <Typography variant="caption" color="text.secondary">
                {lessonName} {lessonGrade && `(Grade ${lessonGrade})`}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography color="text.secondary">Loading expressions...</Typography>
          </Box>
        )}

        {!loading && (
          <>
        {/* Instructions */}
        <Accordion sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="info" fontSize="small" />
              <Typography>Instructions</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" component="div">
              <strong>How to use this editor:</strong>
              <ol>
                <li><strong>Review expressions</strong> - Check the expressions extracted from the lesson</li>
                <li><strong>Select relevant</strong> - Check the box next to expressions that are relevant to learning objectives</li>
                <li><strong>Edit LaTeX</strong> - Modify the LaTeX notation if needed</li>
                <li><strong>Add new</strong> - Use the form at the bottom to add additional expressions</li>
                <li><strong>Save</strong> - Click "Save to S3" to persist your changes</li>
              </ol>
              <strong>LaTeX format examples:</strong>
              <ul>
                <li>Simple: <code>1 + 2 = 3</code></li>
                <li>Fraction: <code>\frac{'{1}'}{'{2}'}</code></li>
                <li>Exponent: <code>2^{'{3}'}</code></li>
                <li>Radical: <code>\sqrt{'{16}'}</code> or <code>\sqrt[3]{'{8}'}</code></li>
                <li>Decimal: <code>3.14</code></li>
              </ul>
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Existing Expressions */}
        <Typography variant="h6" gutterBottom>
          📝 Extracted Expressions
        </Typography>

        {expressions.length === 0 ? (
          <Alert severity="info" sx={{ mb: 3 }}>
            No expressions found. Use the form below to add expressions.
          </Alert>
        ) : (
          <Box sx={{ mb: 3 }}>
            {expressions.map((expr, index) => (
              <Paper 
                key={expr.id} 
                sx={{ 
                  p: 2, 
                  mb: 1.5,
                  bgcolor: expr.selected ? 'action.selected' : 'background.paper',
                  border: 1,
                  borderColor: expr.selected ? 'primary.main' : 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={expr.selected}
                        onChange={(e) => handleSelectChange(index, e.target.checked)}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    value={expr.latex}
                    onChange={(e) => handleLatexChange(index, e.target.value)}
                    placeholder="Enter LaTeX expression... (click to edit)"
                    sx={{
                      fontFamily: 'monospace',
                      '& .MuiInputBase-input': {
                        cursor: 'pointer',
                      }
                    }}
                    onClick={(e) => {
                      // Auto-focus and select text when clicked
                      const input = e.target as HTMLInputElement;
                      setTimeout(() => input.select(), 0);
                    }}
                  />

                  <IconButton
                    onClick={() => handleDeleteExpression(index)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                {expr.context && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 6, display: 'block', mt: 1 }}>
                    Context: {expr.context}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Add New Expression */}
        <Typography variant="h6" gutterBottom>
          ➕ Add New Expression
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={newLatex}
            onChange={(e) => setNewLatex(e.target.value)}
            placeholder="Enter LaTeX expression to add..."
            sx={{ fontFamily: 'monospace' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleAddExpression();
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddExpression}
            disabled={!newLatex.trim()}
          >
            Add
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Tip: Press Ctrl+Enter to add quickly
        </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {expressions.filter(e => e.selected).length} of {expressions.length} selected
          {dirty && ' • Unsaved changes'}
        </Typography>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!dirty || saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
